/**
 * DynamicViewV2 — Main Renderer
 *
 * Composable, source-aware view system. Takes a ViewRecipe prop,
 * maps each ViewBlock to its pre-built React component, and
 * renders them in a responsive grid layout.
 *
 * Falls back gracefully for unknown block types.
 */
import React from 'react';
import type {
  ViewRecipe,
  ViewBlock,
  SourceStripData,
  // TrustBadgeData removed (2026-02-19)
  HeroCardData,
  OptionCarouselData,
  StreamingPlaceholderData,
  LaneAttributionData,
  SourceCardData,
  CitationInlineData,
  FollowUpSuggestionsData,
  ErrorBlockData,
  ActionSpec,
  ActionBarData,
  ComparisonGridData,
  DataTableData,
  ChartBlockData,
  TimelineData,
  StepWizardData,
  BookingCardData,
  ImageGalleryData,
} from './types';
export type { BookingCardData } from './types';

// Block components — Phase 1
import { SourceStrip } from './blocks/SourceStrip';
// TrustBadge removed (2026-02-19)
import { HeroCard } from './blocks/HeroCard';
import { OptionCarousel } from './blocks/OptionCarousel';
import { StreamingPlaceholder } from './blocks/StreamingPlaceholder';
import { LaneAttribution } from './blocks/LaneAttribution';

// Block components — Phase 2 (Trust Layer)
import { SourceCard } from './blocks/SourceCard';
import { CitationInline } from './blocks/CitationInline';
import { FollowUpSuggestions } from './blocks/FollowUpSuggestions';
import { ErrorBlock } from './blocks/ErrorBlock';

// Block components — Phase 3 (Rich Content)
import { ComparisonGrid } from './blocks/ComparisonGrid';
import { DataTable } from './blocks/DataTable';
import { ChartBlock } from './blocks/ChartBlock';
import { Timeline } from './blocks/Timeline';
import { StepWizard } from './blocks/StepWizard';

// Block components — Phase 4 (Actions & Polish)
import { ActionBar } from './blocks/ActionBar';
import { BookingCard } from './blocks/BookingCard';
import { ImageGallery } from './blocks/ImageGallery';

// =============================================================================
// UNKNOWN BLOCK FALLBACK
// =============================================================================

const UnknownBlock: React.FC<{ block: ViewBlock }> = ({ block }) => (
  <div className="rounded-xl border border-cyan-400/10 bg-cyan-400/[0.02] px-4 py-3">
    <span className="text-xs text-cyan-400/40 font-mono">
      Block type &quot;{block.type}&quot; not yet implemented
    </span>
  </div>
);

// =============================================================================
// BLOCK RENDERER
// =============================================================================

interface BlockRendererProps {
  block: ViewBlock;
  recipe: ViewRecipe;
  onAction?: (prompt: string) => void;
  onBookAction?: (bookingData: BookingCardData) => void;
}

const BlockRenderer: React.FC<BlockRendererProps> = ({ block, recipe, onAction, onBookAction }) => {
  // Guard: bail out if block is missing or malformed
  if (!block || typeof block !== 'object' || !block.type) return null;

  try {
    switch (block.type) {
      case 'source_strip':
        return (
          <SourceStrip
            data={block.data as unknown as SourceStripData}
          />
        );

      // trust_badge removed (2026-02-19)

      case 'hero_card':
        return <HeroCard data={block.data as unknown as HeroCardData} />;

      case 'option_carousel':
      case 'option_card':
        return (
          <OptionCarousel
            data={block.data as unknown as OptionCarouselData}
            onAction={onAction}
            onBookAction={onBookAction}
          />
        );

      case 'streaming_placeholder':
        return (
          <StreamingPlaceholder
            data={block.data as unknown as StreamingPlaceholderData}
          />
        );

      case 'lane_attribution':
        return (
          <LaneAttribution
            data={block.data as unknown as LaneAttributionData}
          />
        );

      // Phase 2 — Trust Layer blocks
      case 'source_card':
        return (
          <SourceCard
            data={block.data as unknown as SourceCardData}
            onAction={onAction}
          />
        );

      case 'citation_inline':
        return (
          <CitationInline
            data={block.data as unknown as CitationInlineData}
            sources={Array.isArray(recipe.sources) ? recipe.sources : undefined}
          />
        );

      case 'follow_up_suggestions':
        return (
          <FollowUpSuggestions
            data={block.data as unknown as FollowUpSuggestionsData}
            onAction={onAction}
          />
        );

      case 'error_block':
        return (
          <ErrorBlock
            data={block.data as unknown as ErrorBlockData}
            onAction={onAction}
          />
        );

      // Phase 3 — Rich Content blocks
      case 'comparison_grid':
        return (
          <ComparisonGrid
            data={block.data as unknown as ComparisonGridData}
          />
        );

      case 'data_table':
        return (
          <DataTable
            data={block.data as unknown as DataTableData}
          />
        );

      case 'chart_block':
        return (
          <ChartBlock
            data={block.data as unknown as ChartBlockData}
          />
        );

      case 'timeline':
        return (
          <Timeline
            data={block.data as unknown as TimelineData}
          />
        );

      case 'step_wizard':
        return (
          <StepWizard
            data={block.data as unknown as StepWizardData}
          />
        );

      // Phase 4 — Action & Polish blocks
      case 'action_bar':
        return (
          <ActionBar
            data={block.data as unknown as ActionBarData}
            onAction={onAction}
          />
        );

      case 'booking_card':
        return (
          <BookingCard
            data={block.data as unknown as BookingCardData}
            onBookAction={onBookAction}
          />
        );

      case 'image_gallery':
        return (
          <ImageGallery
            data={block.data as unknown as ImageGalleryData}
          />
        );

      default:
        return <UnknownBlock block={block} />;
    }
  } catch (err) {
    console.error('[DynamicViewV2] Block render error:', block.type, err);
    return null;
  }
};

// =============================================================================
// GLOBAL ACTION BAR (wraps block ActionBar for recipe-level actions)
// =============================================================================

/** Converts top-level ActionSpec[] to ActionBarData for the block component */
function toActionBarData(actions: ActionSpec[]): ActionBarData {
  return {
    actions: actions.map((a) => ({
      id: a.id,
      label: a.label,
      type: a.type,
      url: a.url,
      prompt: a.prompt,
      variant: a.variant,
      icon: a.icon,
    })),
  };
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

interface DynamicViewV2Props {
  recipe: ViewRecipe;
  onAction?: (prompt: string) => void;
  onBookAction?: (bookingData: BookingCardData) => void;
}

export const DynamicViewV2: React.FC<DynamicViewV2Props> = ({ recipe, onAction, onBookAction }) => {
  // Guard: recipe must exist and blocks must be a non-empty array
  // (Firestore can return objects-with-numeric-keys instead of arrays, or strings)
  if (!recipe || !Array.isArray(recipe.blocks) || recipe.blocks.length === 0) {
    return null;
  }

  const safeActions = Array.isArray(recipe.actions) ? recipe.actions : [];

  return (
    <div className="w-full max-w-4xl mx-auto space-y-4">
      {/* Render each block in order */}
      {recipe.blocks.map((block, index) => (
        <div key={`${block?.type ?? 'block'}-${index}`}>
          <BlockRenderer block={block} recipe={recipe} onAction={onAction} onBookAction={onBookAction} />
        </div>
      ))}

      {/* Global actions (if present and no action_bar block already in blocks) */}
      {safeActions.length > 0 &&
        !recipe.blocks.some((b) => b?.type === 'action_bar') && (
          <ActionBar data={toActionBarData(safeActions)} onAction={onAction} />
        )}
    </div>
  );
};

export default DynamicViewV2;
